import type {
  BackgroundJobsCountBody,
  BackgroundJobsListBody,
} from '@/api/(admin)/background-jobs/types';
import type { BackgroundJobsQueryInput } from '../-types';

/**
 * The endpoint also orders by `scheduled_at` and `priority`, but history has
 * no index for either: one header click would scan all of it, again on every
 * auto-refresh. Only the indexed column is offered.
 */
const SORT_FIELDS: BackgroundJobsListBody['sort'][] = ['created_at'];

/**
 * Turns the table's state into the endpoint's body.
 *
 * The single conversion point: the query key is the object this returns, so
 * the route loader (reading the URL) and the page hook (reading `useTable`)
 * only share a cache entry if both derive it here.
 *
 *  - **Page base.** No `-1`: the endpoint counts from 1.
 *  - **Sort.** Any column the endpoint can't order by is dropped rather than
 *    sent — `sort` is an enum and an unknown value 400s the whole request.
 *  - **Filters.** Exact matches, so only the first value of each is sent.
 */
export function toBackgroundJobsBody(
  input: BackgroundJobsQueryInput,
): BackgroundJobsListBody {
  const [field, direction] = (input.sort ?? '').split(':');
  const sort = SORT_FIELDS.find(f => f === field);

  const jobName = firstValue(input.filters?.jobName);
  const status = firstValue(input.filters?.status);
  const handler = firstValue(input.filters?.handler);

  return {
    page: Math.max(1, input.page),
    limit: input.limit,
    sort: sort ?? 'created_at',
    order: sort && direction?.toUpperCase() === 'ASC' ? 'asc' : 'desc',
    ...(jobName ? { job_name: jobName } : {}),
    ...(status ? { status } : {}),
    ...(handler ? { processor_namespace: handler } : {}),
  };
}

/**
 * The count endpoint's body: the list's filters and nothing else. It is the
 * count query's key, so turning a page or changing the sort reuses the cached
 * total instead of counting again.
 */
export function toBackgroundJobsCountBody(
  input: BackgroundJobsQueryInput,
): BackgroundJobsCountBody {
  const { job_name, status, processor_namespace } = toBackgroundJobsBody(input);
  return {
    ...(job_name ? { job_name } : {}),
    ...(status ? { status } : {}),
    ...(processor_namespace ? { processor_namespace } : {}),
  };
}

function firstValue(values: string[] | undefined): string | undefined {
  const value = values?.[0]?.trim();
  return value ? value : undefined;
}
